import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Card, Button, ProgressBar, Alert, Modal, Badge, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  Quiz,
  Question,
  quizService,
  QuizAttemptResponse,
  UnlockedAchievementPayload,
  UnlockedCharacterPayload,
  LoginStreakHome,
  authService,
  pvpService,
  type HostedOpenAsyncPvPSummary,
  type WitcFinishAudit,
} from '../services/api';
import { useWitcWasm } from '../hooks/useWitcWasm';
import QuizFinishCelebrationModal from './QuizFinishCelebrationModal';
import LevelUpCelebrationModal from './LevelUpCelebrationModal';
import { useQuizFinishLevelUp } from '../hooks/useQuizFinishLevelUp';
import { useQuizTimer } from '../hooks/useQuizTimer';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { isPwaStandalone } from '../utils/pwaStandalone';
import SoloQuizResultsModal from './SoloQuizResultsModal';
import { quizQuestionImageUrlsFromKey, WITC_SILHOUETTE_FILTER_CSS } from '../utils/quizQuestionImageSrc';
import { quizQuestionPanelBackground } from '../utils/quizQuestionPanelBackground';
import { WITC_REVEAL_DELAY_MS } from '../utils/witcExpectedAnswer';
import { getQuizFullDisplayCaption } from '../utils/quizDisplayTitle';
import QuizPlayPageHeading from './QuizPlayPageHeading';
import {
  normalizePlayDifficultyLabel,
  maxPointsForPlayDifficultyBand,
  witcMaxWrongBeforeExhaustForBand,
  witcTimeLimitSecondsForBand,
} from '../utils/quizPlayBandScoring';
import { pickWitcMediumSkeleton } from '../utils/witcMediumHints';
import WitcMediumAnswerHintStrip from './WitcMediumAnswerHintStrip';
import QuizSoundToggleButton from './QuizSoundToggleButton';
import NavbarMusicToggle from './NavbarMusicToggle';
import { useTranslation } from 'react-i18next';
import { playQuizSfxCorrect, playQuizSfxIncorrect, playWitcTypewriterKeystrokeSfx } from '../audio/quizSfx';
import { onQuizSkipClick, useBlurMobileSkipFocusOnQuestionChange } from '../utils/quizSkipButton';
import {
  buildSoloArenaHostCompleteBody,
  type SoloArenaPublishSnapshot,
} from '../utils/soloArenaPublishLayout';

import './shared/QuizStyles.css';
import './QuizPlayer.css';

export interface WitcQuizPlayerProps {
  quizId: string;
  quiz: Quiz;
  questions: Question[];
  startTime: Date;
  /** Solo Witc: from POST /quiz/{id}/start — required for server-side wrong-count scope (no cheating across runs). */
  witcPlaySessionId?: string | null;
  witcEngineBootstrapCipher?: string | null;
  /** Band used this run — sent with save attempt for server grading (multi-difficulty quizzes). */
  playDifficulty?: string | null;
  /** Solo quick match: one band per question (same order as questions). */
  playDifficultyPerQuestion?: string[] | null;
  soloQuickMatch?: boolean;
  /** Re-run solo start (lives, session id, questions) and remount — provided by QuizPlayer. */
  onPlayAgain: () => void | Promise<void>;
  /** While a new session is starting from results modal. */
  playAgainLoading?: boolean;
  /** Partida rápida mista: uma pergunta WITC por vez; ao terminar o slot chama callback em vez de salvar. */
  mixedQuickMatchSlot?: boolean;
  onMixedQuickMatchSlotComplete?: (payload: {
    mergedAnswers: Map<number, string>;
    witcResult: { pointsEarned: number; finalAnswerText?: string | null };
    responseSeconds: number;
  }) => void;
  /** Cabeçalho quando o pai mostra pontuação global (quick match misto). */
  mixedSlotHeader?: {
    displayScore: number;
    displayCorrect: number;
    questionOneBased: number;
    totalQuestions: number;
  };
  /** QuizPlayer mostra aviso de poção — esconder resultados por baixo */
  suppressResultsModal?: boolean;
  /** Parent ref — solo Witc fills after save so Play Again / Voltar runs the same Arena publish as MC. */
  arenaPublishSnapshotRef?: React.MutableRefObject<SoloArenaPublishSnapshot | null>;
  /** Shared with QuizPlayer so “Play Again” uses the same checkbox ref as MC. */
  arenaPublishChecked?: boolean;
  onArenaPublishCheckedChange?: (checked: boolean) => void;
  arenaPublishCheckedRef?: React.MutableRefObject<boolean>;
  embeddedInPvp?: boolean;
  onPvpQuitRequest?: () => void;
  pvpAsync?: {
    flow: 'host' | 'opponent';
    roomId: string;
    onComplete: (payload: {
      score: number;
      correctAnswers: number;
      totalQuestions: number;
      durationSeconds: number;
      skippedQuestions: number;
      questionIdsInOrder: string[];
      responseTimeSecondsPerQuestion: number[];
      correctPerQuestion: boolean[];
      witcFinishAudit?: WitcFinishAudit | null;
      witcResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }> | null;
    }) => Promise<void> | void;
  };
}

const WitcQuizPlayer: React.FC<WitcQuizPlayerProps> = ({
  quizId,
  quiz,
  questions,
  startTime,
  witcPlaySessionId,
  witcEngineBootstrapCipher,
  playDifficulty,
  playDifficultyPerQuestion,
  soloQuickMatch = false,
  onPlayAgain,
  playAgainLoading = false,
  mixedQuickMatchSlot = false,
  onMixedQuickMatchSlotComplete,
  mixedSlotHeader,
  suppressResultsModal = false,
  arenaPublishSnapshotRef,
  arenaPublishChecked: arenaPublishCheckedProp,
  onArenaPublishCheckedChange: onArenaPublishCheckedChangeProp,
  arenaPublishCheckedRef: arenaPublishCheckedRefProp,
  embeddedInPvp = false,
  onPvpQuitRequest,
  pvpAsync,
}) => {
  const { t } = useSafeTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quickMatchSlotCipher, setQuickMatchSlotCipher] = useState<string | null>(null);
  const [quickMatchSlotCipherError, setQuickMatchSlotCipherError] = useState<string | null>(null);

  const effectiveWitcCipher = witcEngineBootstrapCipher ?? quickMatchSlotCipher ?? undefined;

  const {
    ready: wasmReady,
    error: wasmError,
    submitAnswer: wasmSubmitAnswer,
    buildFinishPayload,
  } = useWitcWasm(effectiveWitcCipher);

  useEffect(() => {
    if (!mixedQuickMatchSlot || witcEngineBootstrapCipher?.trim() || !questions[0]?.id) {
      setQuickMatchSlotCipher(null);
      setQuickMatchSlotCipherError(null);
      return;
    }

    let cancelled = false;
    const band =
      (playDifficultyPerQuestion?.[0] && playDifficultyPerQuestion[0].trim()) ||
      (playDifficulty && playDifficulty.trim()) ||
      quiz.difficulty ||
      'Easy';

    setQuickMatchSlotCipher(null);
    setQuickMatchSlotCipherError(null);

    (async () => {
      try {
        const start = await quizService.startQuickMatchWitcSlot(questions[0].id, band);
        if (cancelled) return;
        if (!start.engineBootstrapCipher?.trim()) {
          setQuickMatchSlotCipherError('WITC engine failed to load.');
          return;
        }
        setQuickMatchSlotCipher(start.engineBootstrapCipher);
      } catch (e) {
        if (cancelled) return;
        setQuickMatchSlotCipherError(
          e instanceof Error ? e.message : 'WITC engine failed to load.'
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    mixedQuickMatchSlot,
    witcEngineBootstrapCipher,
    questions,
    playDifficulty,
    playDifficultyPerQuestion,
    quiz.difficulty,
  ]);
  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [wrongFlash, setWrongFlash] = useState(false);
  const [correctFlash, setCorrectFlash] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [score, setScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [saveAttemptError, setSaveAttemptError] = useState<string | null>(null);
  const [savingQuizAttempt, setSavingQuizAttempt] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultDurationSeconds, setResultDurationSeconds] = useState<number | null>(null);
  const [quizResponse, setQuizResponse] = useState<QuizAttemptResponse | null>(null);
  const [earnedXP, setEarnedXP] = useState(0);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [hasTooManySkips, setHasTooManySkips] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationAchievements, setCelebrationAchievements] = useState<UnlockedAchievementPayload[]>([]);
  const [celebrationCharacters, setCelebrationCharacters] = useState<UnlockedCharacterPayload[]>([]);
  const [celebrationLoginStreak, setCelebrationLoginStreak] = useState<LoginStreakHome | null>(null);
  const [celebrationPlayerNick, setCelebrationPlayerNick] = useState('');
  const levelUpCelebration = useQuizFinishLevelUp();
  const [fallbackArenaPublishChecked, setFallbackArenaPublishChecked] = useState(true);
  const fallbackArenaPublishCheckedRef = useRef(true);
  const arenaPublishChecked = arenaPublishCheckedProp ?? fallbackArenaPublishChecked;
  const setArenaPublishChecked = onArenaPublishCheckedChangeProp ?? setFallbackArenaPublishChecked;
  const arenaPublishCheckedRef = arenaPublishCheckedRefProp ?? fallbackArenaPublishCheckedRef;
  const [hostedOpenSummaryForArena, setHostedOpenSummaryForArena] =
    useState<HostedOpenAsyncPvPSummary | null>(null);
  const [arenaHostedSummaryLoading, setArenaHostedSummaryLoading] = useState(false);
  /** Hide image until decoded + painted with brightness mask (avoids spoiler frame). */
  const [witcImageRevealReady, setWitcImageRevealReady] = useState(false);
  /** Try `.webp` first, then `.png` if the asset is missing. */
  const [witcImageExt, setWitcImageExt] = useState<'webp' | 'png'>('webp');
  /** After a correct answer, show full-color image (remove silhouette) before advancing. */
  const [revealFullImage, setRevealFullImage] = useState(false);
  /** After max wrong attempts: show answer on a ribbon, then advance (see WITC_REVEAL_DELAY_MS). */
  const [exhaustRevealAnswer, setExhaustRevealAnswer] = useState<string | null>(null);
  /** Easy/Medium: index → character returned by API (Easy: 1st wrong; Medium: 2nd wrong). */
  const [mediumHintByIndex, setMediumHintByIndex] = useState<Record<number, string>>({});
  const finishQuizInFlightRef = useRef(false);
  const questionShownAtRef = useRef<number>(Date.now());
  const secondsToAnswerRef = useRef<number[]>([]);
  const witcResultsRef = useRef<Array<{ pointsEarned: number; finalAnswerText?: string }>>([]);
  const answersRef = useRef<Map<number, string>>(new Map());
  const answerInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    arenaPublishCheckedRef.current = arenaPublishChecked;
  }, [arenaPublishChecked, arenaPublishCheckedRef]);

  const arenaPublishOffer = useMemo(
    () =>
      isQuizCompleted &&
      !hasTooManySkips &&
      !mixedQuickMatchSlot &&
      !soloQuickMatch &&
      questions.length > 0 &&
      correctAnswers / questions.length >= 0.7,
    [isQuizCompleted, hasTooManySkips, mixedQuickMatchSlot, soloQuickMatch, questions.length, correctAnswers]
  );

  const arenaPublishBlockedOpenQuiz = useMemo(() => {
    const qid = quiz?.id?.trim();
    if (!qid || !hostedOpenSummaryForArena?.openQuizIds?.length) return false;
    const lower = qid.toLowerCase();
    return hostedOpenSummaryForArena.openQuizIds.some((id) => id.trim().toLowerCase() === lower);
  }, [quiz?.id, hostedOpenSummaryForArena]);

  const arenaPublishBlockedMaxRooms = useMemo(() => {
    if (!hostedOpenSummaryForArena) return false;
    const max = hostedOpenSummaryForArena.maxOpenRooms;
    return hostedOpenSummaryForArena.openCount >= max;
  }, [hostedOpenSummaryForArena]);

  const arenaPublishBlocked = arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms;

  const arenaPublishBlockedReason = arenaPublishBlockedOpenQuiz
    ? ('duplicateQuiz' as const)
    : arenaPublishBlockedMaxRooms
      ? ('maxRooms' as const)
      : undefined;

  useEffect(() => {
    if (arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms) {
      setArenaPublishChecked(false);
    }
  }, [arenaPublishBlockedOpenQuiz, arenaPublishBlockedMaxRooms, setArenaPublishChecked]);

  useEffect(() => {
    if (!showResultsModal || !arenaPublishOffer || !isQuizCompleted || hasTooManySkips) {
      setHostedOpenSummaryForArena(null);
      setArenaHostedSummaryLoading(false);
      return;
    }
    let cancelled = false;
    setArenaHostedSummaryLoading(true);
    void pvpService
      .getHostedOpenAsyncSummary()
      .then((s) => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(s);
          setArenaHostedSummaryLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(null);
          setArenaHostedSummaryLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showResultsModal, arenaPublishOffer, isQuizCompleted, hasTooManySkips]);

  const executeArenaPublishFromSoloIfNeeded = useCallback(async () => {
    const snap = arenaPublishSnapshotRef?.current;
    if (!snap || !arenaPublishCheckedRef.current) return;
    const qidForGate = snap.quizId?.trim();
    try {
      const summaryFresh = await pvpService.getHostedOpenAsyncSummary();
      if (summaryFresh.openCount >= summaryFresh.maxOpenRooms) return;
      if (
        qidForGate &&
        summaryFresh.openQuizIds.some((id) => id.trim().toLowerCase() === qidForGate.toLowerCase())
      )
        return;
    } catch {
      return;
    }
    try {
      const band = normalizePlayDifficultyLabel(snap.playDifficulty) || 'Easy';
      const room = await pvpService.createAsyncPvPRoom({
        name: snap.roomDisplayName.trim() || 'PvP',
        quizId: snap.quizId,
        playDifficulty: band,
      });
      await pvpService.completeAsyncHostQuiz(
        room.id,
        buildSoloArenaHostCompleteBody(snap),
        { pwaClient: isPwaStandalone() }
      );
      toast.success(t('quiz.soloArenaPublishSuccess', 'Room listed on the PvP Arena.'));
      if (arenaPublishSnapshotRef) arenaPublishSnapshotRef.current = null;
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) &&
        err.response?.data &&
        typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : t('quiz.soloArenaPublishError', 'Could not publish to the Arena.');
      toast.error(msg);
    }
  }, [t, arenaPublishSnapshotRef]);

  const activeBand =
    soloQuickMatch &&
    playDifficultyPerQuestion &&
    playDifficultyPerQuestion.length === questions.length &&
    questions[currentQuestionIndex]
      ? playDifficultyPerQuestion[currentQuestionIndex]
      : playDifficulty ?? quiz.difficulty;
  const maxPoints = maxPointsForPlayDifficultyBand(activeBand);
  const witcMaxWrongBeforeExhaust = witcMaxWrongBeforeExhaustForBand(activeBand);
  const timeLimitSeconds = witcTimeLimitSecondsForBand(activeBand);
  const playBandLabel = normalizePlayDifficultyLabel(activeBand);

  const witcPanelBg = useMemo(
    () => quizQuestionPanelBackground(currentQuestionIndex, questions[currentQuestionIndex]?.id ?? ''),
    [currentQuestionIndex, questions]
  );

  const handleWitcAnswerChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setInputValue((prev) => {
      if (next.length > prev.length) {
        playWitcTypewriterKeystrokeSfx();
      }
      return next;
    });
  }, []);

  const handleTimeout = () => {
    if (!isQuizCompleted) {
      handleSkipQuestion();
    }
  };

  const { timeLeft, resetTimer, stopTimer } = useQuizTimer({
    timeLimit: timeLimitSeconds,
    onTimeout: handleTimeout,
    isActive:
      !isQuizCompleted &&
      currentQuestionIndex < questions.length &&
      exhaustRevealAnswer === null &&
      !(submitting && correctFlash),
  });

  // Only when the question changes — do not list resetTimer here: it is a new function every render
  // from useQuizTimer, so including it would re-run this effect every tick and clear the input.
  useEffect(() => {
    questionShownAtRef.current = Date.now();
    resetTimer();
    setInputValue('');
    setErrorCount(0);
    setWrongFlash(false);
    setCorrectFlash(false);
    setRevealFullImage(false);
    setExhaustRevealAnswer(null);
    setMediumHintByIndex({});
    setWitcImageExt('webp');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: stable reset on question index only
  }, [currentQuestionIndex, questions.length]);

  useBlurMobileSkipFocusOnQuestionChange(currentQuestionIndex);

  /** If parent props change (e.g. fewer questions), keep index in range so current question is never undefined. */
  useEffect(() => {
    if (questions.length === 0) return;
    if (currentQuestionIndex >= questions.length) {
      setCurrentQuestionIndex(Math.max(0, questions.length - 1));
    }
  }, [questions.length, currentQuestionIndex]);

  /** Keep focus on the answer field: new question, or after submit ends (e.g. wrong answer). */
  useEffect(() => {
    if (
      isQuizCompleted ||
      savingQuizAttempt ||
      submitting ||
      exhaustRevealAnswer !== null ||
      showQuitModal ||
      showResultsModal ||
      showCelebration
    ) {
      return;
    }
    const id = requestAnimationFrame(() => {
      answerInputRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [
    currentQuestionIndex,
    submitting,
    isQuizCompleted,
    savingQuizAttempt,
    showQuitModal,
    showResultsModal,
    showCelebration,
    exhaustRevealAnswer,
  ]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setCelebrationPlayerNick('');
      return;
    }
    authService
      .getProfile()
      .then((u) => {
        const nick = (u.nickname && u.nickname.trim()) || u.name || '';
        setCelebrationPlayerNick(nick);
      })
      .catch(() => setCelebrationPlayerNick(''));
  }, []);

  const recordSecondsForCurrentQuestion = () => {
    const elapsed = Math.min(
      timeLimitSeconds,
      Math.max(0, Math.floor((Date.now() - questionShownAtRef.current) / 1000))
    );
    secondsToAnswerRef.current[currentQuestionIndex] = elapsed;
  };

  const handleSkipQuestion = () => {
    if (savingQuizAttempt) return;
    recordSecondsForCurrentQuestion();
    const merged = new Map(answersRef.current);
    merged.set(currentQuestionIndex, 'skip');
    answersRef.current = merged;
    witcResultsRef.current[currentQuestionIndex] = { pointsEarned: 0 };

    if (currentQuestionIndex < questions.length - 1) {
      setErrorCount(0);
      setWrongFlash(false);
      setCorrectFlash(false);
      setRevealFullImage(false);
      setWitcImageRevealReady(false);
      setCurrentQuestionIndex((i) => i + 1);
    } else {
      if (mixedQuickMatchSlot && onMixedQuickMatchSlotComplete) {
        onMixedQuickMatchSlotComplete({
          mergedAnswers: merged,
          witcResult: { pointsEarned: 0 },
          responseSeconds: secondsToAnswerRef.current[currentQuestionIndex],
        });
        return;
      }
      void finishQuiz(merged);
    }
  };

  const advanceAfterResult = (pointsEarned: number, finalText: string | undefined, wasCorrect: boolean) => {
    recordSecondsForCurrentQuestion();
    const merged = new Map(answersRef.current);
    merged.set(currentQuestionIndex, 'witc');
    answersRef.current = merged;
    witcResultsRef.current[currentQuestionIndex] = {
      pointsEarned,
      ...(pointsEarned > 0 && finalText ? { finalAnswerText: finalText } : {}),
    };
    if (!(mixedQuickMatchSlot && onMixedQuickMatchSlotComplete)) {
      setScore((s) => s + pointsEarned);
      if (wasCorrect) setCorrectAnswers((c) => c + 1);
    }

    if (currentQuestionIndex < questions.length - 1) {
      setErrorCount(0);
      setWrongFlash(false);
      setCorrectFlash(false);
      setRevealFullImage(false);
      setWitcImageRevealReady(false);
      setCurrentQuestionIndex((i) => i + 1);
    } else {
      if (mixedQuickMatchSlot && onMixedQuickMatchSlotComplete) {
        onMixedQuickMatchSlotComplete({
          mergedAnswers: merged,
          witcResult: witcResultsRef.current[currentQuestionIndex],
          responseSeconds: secondsToAnswerRef.current[currentQuestionIndex],
        });
        return;
      }
      void finishQuiz(merged);
    }
  };

  const finishQuiz = async (answersForSubmit: Map<number, string>) => {
    if (finishQuizInFlightRef.current) return;
    finishQuizInFlightRef.current = true;
    setSavingQuizAttempt(true);
    stopTimer();
    const finishedAt = new Date();
    setIsQuizCompleted(true);
    const duration = Math.max(0, Math.floor((finishedAt.getTime() - startTime.getTime()) / 1000));
    setResultDurationSeconds(duration);

    const answersArray = Array.from({ length: questions.length }, (_, index) => answersForSubmit.get(index) || 'skip');
    const responseTimes = Array.from({ length: questions.length }, (_, i) => {
      const band =
        soloQuickMatch && playDifficultyPerQuestion && playDifficultyPerQuestion.length === questions.length
          ? playDifficultyPerQuestion[i]
          : playDifficulty ?? quiz.difficulty;
      const limit = witcTimeLimitSecondsForBand(band);
      return secondsToAnswerRef.current[i] ?? limit;
    });
    const witcResults = questions.map((_, i) => witcResultsRef.current[i] ?? { pointsEarned: 0 });
    const totalScore = witcResults.reduce((s, r) => s + r.pointsEarned, 0);
    const totalCorrect = witcResults.filter((r) => r.pointsEarned > 0).length;
    setScore(totalScore);
    setCorrectAnswers(totalCorrect);

    if (!wasmReady) {
      finishQuizInFlightRef.current = false;
      setIsQuizCompleted(false);
      setSaveAttemptError('WITC finish audit is missing. Reload the quiz and try again.');
      setSavingQuizAttempt(false);
      return;
    }
    const witcFinishAudit = buildFinishPayload();
    if (!witcFinishAudit) {
      finishQuizInFlightRef.current = false;
      setIsQuizCompleted(false);
      setSaveAttemptError('WITC finish audit is missing. Reload the quiz and try again.');
      setSavingQuizAttempt(false);
      return;
    }

    if (embeddedInPvp && pvpAsync) {
      const skippedCount = responseTimes.filter((t, idx) => 
        (witcResults[idx]?.pointsEarned ?? 0) === 0 && t === 0
      ).length;
      try {
        await pvpAsync.onComplete({
          score: totalScore,
          correctAnswers: totalCorrect,
          totalQuestions: questions.length,
          durationSeconds: duration,
          skippedQuestions: skippedCount,
          questionIdsInOrder: questions.map((q) => q.id),
          responseTimeSecondsPerQuestion: responseTimes,
          correctPerQuestion: questions.map((_, i) => (witcResults[i]?.pointsEarned ?? 0) > 0),
          witcFinishAudit,
          witcResults,
        });
      } catch (error) {
        toast.error('Failed to submit PvP results.');
      } finally {
        setSavingQuizAttempt(false);
        finishQuizInFlightRef.current = false;
      }
      return;
    }

    try {
      const saveQuizId =
        soloQuickMatch && questions.length > 0 ? questions[0].quizId : quizId;
      const response = await quizService.saveQuizAttempt(
        {
          quizId: saveQuizId,
          score: totalScore,
          correctAnswers: totalCorrect,
          totalQuestions: questions.length,
          duration,
          isCompleted: true,
          answers: answersArray,
          questionIdsInOrder: questions.map((q) => q.id),
          responseTimeSecondsPerQuestion: responseTimes,
          witcResults,
          playDifficulty: soloQuickMatch ? undefined : playDifficulty ?? quiz.difficulty,
          soloQuickMatch: soloQuickMatch || undefined,
          playDifficultiesPerQuestion:
            soloQuickMatch && playDifficultyPerQuestion && playDifficultyPerQuestion.length === questions.length
              ? playDifficultyPerQuestion
              : undefined,
          witcFinishAudit,
        },
        { pwaClient: isPwaStandalone() }
      );
      setQuizResponse(response);
      if (response.earnedXP !== undefined) {
        setEarnedXP(response.earnedXP);
      }
      if (response.tooManySkips) {
        setHasTooManySkips(true);
        setIsQuizCompleted(false);
      }
      if (arenaPublishSnapshotRef && !soloQuickMatch) {
        arenaPublishSnapshotRef.current = null;
        if (
          !response.tooManySkips &&
          questions.length > 0 &&
          totalCorrect / questions.length >= 0.7 &&
          quiz
        ) {
          const bandSingle = playDifficulty ?? quiz.difficulty ?? 'Easy';
          arenaPublishSnapshotRef.current = {
            quizId: saveQuizId,
            playDifficulty: bandSingle,
            roomDisplayName: (quiz.title || 'Arena').trim().slice(0, 80),
            score: totalScore,
            correctAnswers: totalCorrect,
            totalQuestions: questions.length,
            durationSeconds: duration,
            skippedQuestions: answersArray.filter((a) => a === 'skip').length,
            questionIdsInOrder: questions.map((q) => q.id),
            answerIdsPerQuestion: questions.map(() => []),
            responseTimeSecondsPerQuestion: responseTimes,
            correctPerQuestion: questions.map((_, i) => (witcResults[i]?.pointsEarned ?? 0) > 0),
            witcFinishAudit,
            witcResults: witcResults.map((r) => ({
              pointsEarned: r.pointsEarned,
              finalAnswerText: r.finalAnswerText ?? null,
            })),
          };
        }
      }
      setArenaPublishChecked(true);
      const unlocked = response.unlockedAchievements?.filter(Boolean) ?? [];
      const unlockedChars = response.unlockedCharacters?.filter(Boolean) ?? [];
      const streakUpdate = response.loginStreakUpdated ?? null;
      const hasLevelUp = levelUpCelebration.prepareFromQuizResponse(response);
      if ((unlocked.length > 0 || unlockedChars.length > 0 || streakUpdate) && !response.tooManySkips) {
        setCelebrationAchievements(unlocked);
        setCelebrationCharacters(unlockedChars);
        setCelebrationLoginStreak(streakUpdate);
        setShowCelebration(true);
      } else if (hasLevelUp && levelUpCelebration.tryOpenLevelUpCelebration()) {
        /* level-up modal */
      } else {
        setShowResultsModal(true);
      }
    } catch (error: unknown) {
      finishQuizInFlightRef.current = false;
      setIsQuizCompleted(false);
      const msg =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String((error.response.data as { message?: string }).message)
          : error instanceof Error
            ? error.message
            : 'Unknown error';
      setSaveAttemptError(`Failed to save quiz results: ${msg}`);
    } finally {
      setSavingQuizAttempt(false);
    }
  };

  const onSubmitAnswer = async () => {
    if (submitting || savingQuizAttempt || isQuizCompleted) return;
    const text = inputValue.trim();
    if (!text) return;
    let releaseSubmittingInFinally = true;
    setSubmitting(true);
    try {
      const q = questions[currentQuestionIndex];
      if (!wasmReady) return;

      const res = await wasmSubmitAnswer(q.id, text, i18n.language);
      if (!res) return;

      if (res.isCorrect) {
        playQuizSfxCorrect();
        const earned = res.pointsEarned;
        setRevealFullImage(true);
        setCorrectFlash(true);
        releaseSubmittingInFinally = false;
        setTimeout(() => {
          setCorrectFlash(false);
          advanceAfterResult(earned, text, true);
          setSubmitting(false);
        }, WITC_REVEAL_DELAY_MS);
      } else {
        playQuizSfxIncorrect();
        const nextErrors = res.errorCountAfter;
        setErrorCount(nextErrors);
        setWrongFlash(true);
        setTimeout(() => setWrongFlash(false), 650);
        setInputValue('');

        if (res.mediumHintLetters && res.mediumHintLetters.length > 0) {
          setMediumHintByIndex((prev) => {
            const next = { ...prev };
            for (const h of res.mediumHintLetters!) {
              next[h.index] = h.char;
            }
            return next;
          });
        }

        if (res.isExhausted) {
          releaseSubmittingInFinally = false;
          const runExhaust = () => {
            setRevealFullImage(true);
            const revealed = (res.revealedAnswer ?? '').trim();
            setExhaustRevealAnswer(revealed || '—');
            setTimeout(() => {
              setExhaustRevealAnswer(null);
              advanceAfterResult(0, undefined, false);
              setSubmitting(false);
            }, WITC_REVEAL_DELAY_MS);
          };
          runExhaust();
        }
      }
    } catch {
      playQuizSfxIncorrect();
      setWrongFlash(true);
      setInputValue('');
      setTimeout(() => setWrongFlash(false), 650);
    } finally {
      if (releaseSubmittingInFinally) {
        setSubmitting(false);
      }
    }
  };

  const handleQuitQuiz = () => {
    if (embeddedInPvp && onPvpQuitRequest) {
      onPvpQuitRequest();
      return;
    }
    setShowQuitModal(true);
  };

  const confirmQuit = () => {
    stopTimer();
    const quitAt = new Date();
    const secs = Math.max(0, Math.floor((quitAt.getTime() - startTime.getTime()) / 1000));
    setResultDurationSeconds(secs);
    setIsQuizCompleted(false);
    setShowQuitModal(false);
    setShowResultsModal(true);
  };

  const handleBackToHome = async () => {
    await executeArenaPublishFromSoloIfNeeded();
    setShowResultsModal(false);
    setShowCelebration(false);
    setCelebrationAchievements([]);
    setCelebrationCharacters([]);
    setCelebrationLoginStreak(null);
    levelUpCelebration.clearPendingLevelUp();
    navigate('/', { replace: true });
  };

  const handleCelebrationFinished = () => {
    setShowCelebration(false);
    setCelebrationAchievements([]);
    setCelebrationCharacters([]);
    setCelebrationLoginStreak(null);
    if (levelUpCelebration.tryOpenLevelUpCelebration()) {
      return;
    }
    setShowResultsModal(true);
  };

  const handleLevelUpFinished = () => {
    levelUpCelebration.closeLevelUpCelebration();
    setShowResultsModal(true);
  };

  /** Decode + paint with silhouette mask while invisible; then fade in (no color spoiler). */
  const handleWitcImageLoaded = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void el.offsetHeight;
        requestAnimationFrame(() => {
          setWitcImageRevealReady(true);
        });
      });
    });
  };

  const q = questions[currentQuestionIndex];
  const witcMediumSkeleton =
    (playBandLabel === 'Medium' || playBandLabel === 'Easy') && q
      ? pickWitcMediumSkeleton(q, i18n.language)
      : '';
  const basePath = q?.soloQuestionImageBasePath?.trim() || quiz.witcImageAssetsBasePath || '';
  const witcImageUrls = useMemo(
    () => quizQuestionImageUrlsFromKey(basePath, q?.imageAssetKey ?? ''),
    [basePath, q?.imageAssetKey]
  );
  const imgSrc =
    witcImageUrls && (witcImageExt === 'webp' ? witcImageUrls.webp : witcImageUrls.png);
  const usePt = i18n.language?.toLowerCase().startsWith('pt');
  const questionLabel = q ? (usePt ? q.text : q.textEn || q.text) : '';
  const pointsLeft = Math.max(0, maxPoints - errorCount);
  const progressPercent = mixedSlotHeader
    ? (mixedSlotHeader.questionOneBased / mixedSlotHeader.totalQuestions) * 100
    : questions.length > 0
      ? ((currentQuestionIndex + 1) / questions.length) * 100
      : 0;
  const headerScore = mixedSlotHeader?.displayScore ?? score;
  const headerCorrect = mixedSlotHeader?.displayCorrect ?? correctAnswers;
  const headerQuestionCurrent = mixedSlotHeader?.questionOneBased ?? currentQuestionIndex + 1;
  const headerQuestionTotal = mixedSlotHeader?.totalQuestions ?? questions.length;

  if (saveAttemptError) {
    return (
      <div className="quiz-error">
        <Alert variant="danger">
          <Alert.Heading>{t('common.error', 'Error')}</Alert.Heading>
          <p>{saveAttemptError}</p>
          <Button variant="outline-danger" onClick={handleBackToHome}>
            {t('quiz.backToHome', 'Back to Home')}
          </Button>
        </Alert>
      </div>
    );
  }

  if (wasmError || quickMatchSlotCipherError) {
    return (
      <div className="quiz-page d-flex flex-column align-items-center justify-content-center min-vh-50 text-white">
        <Alert variant="danger" className="mx-3 text-center">
          {wasmError || quickMatchSlotCipherError}
        </Alert>
      </div>
    );
  }

  if (!wasmReady || !q || questions.length === 0) {
    return (
      <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
        <div className="quiz-finish-saving-overlay__inner">
          <Spinner animation="border" role="presentation" />
          <span className="text-white mt-2 small">{t('quiz.loadingQuiz', 'Loading game...')}</span>
        </div>
      </div>
    );
  }

  const witcPlayBody = (
    <div className={embeddedInPvp ? 'witc-quiz-player-embedded witc-quiz-player' : 'quiz-container'}>
      <div className="quiz-header quiz-page-header">
        {!embeddedInPvp && <QuizPlayPageHeading quiz={quiz} language={i18n.language} />}
        <div className="quiz-page-header__meta d-flex align-items-center justify-content-between gap-2">
          <div className="d-flex gap-2 align-items-center flex-wrap min-w-0 flex-grow-1">
            <Badge
              bg={
                playBandLabel === 'Easy'
                  ? 'success'
                  : playBandLabel === 'Medium'
                    ? 'warning'
                    : 'danger'
              }
            >
              {t(`quiz.${playBandLabel.toLowerCase()}`, playBandLabel)}
            </Badge>
            <span className="text-muted quiz-page-header__question-of">
              {t('quiz.questionOf', 'Question {{current}} of {{total}}')
                .replace('{{current}}', String(headerQuestionCurrent))
                .replace('{{total}}', String(headerQuestionTotal))}
            </span>
            <Badge bg={timeLeft <= 10 ? 'danger' : timeLeft <= 20 ? 'warning' : 'info'} className="timer-badge">
              {t('quiz.timeSeconds', '{{time}}s').replace('{{time}}', String(timeLeft))}
            </Badge>
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            {!embeddedInPvp && <NavbarMusicToggle className="flex-shrink-0" />}
            {!embeddedInPvp && <QuizSoundToggleButton disabled={savingQuizAttempt} />}
            <Button
              type="button"
              variant="outline-danger"
              onClick={handleQuitQuiz}
              className="quiz-quit-icon-btn flex-shrink-0"
              aria-label={t('quiz.quitQuiz', 'Quit Quiz')}
            >
              <i className="bi bi-x-lg" aria-hidden />
            </Button>
          </div>
        </div>
      </div>

      <ProgressBar
        now={progressPercent}
        className="quiz-progress-bar"
        label={`${Math.round(progressPercent)}%`}
      />

      <Card className="question-card quiz-compact mb-3">
        <Card.Body>
          <Card.Title className="question-title h5">{questionLabel}</Card.Title>
          <div
            className="witc-image-container mb-3 rounded overflow-hidden position-relative d-flex align-items-center justify-content-center"
            style={{ maxHeight: 320, minHeight: 200, backgroundColor: witcPanelBg }}
            aria-busy={imgSrc ? !witcImageRevealReady : undefined}
          >
            <Button
              type="button"
              variant="light"
              size="sm"
              onClick={onQuizSkipClick(handleSkipQuestion)}
              disabled={savingQuizAttempt || exhaustRevealAnswer !== null}
              className="witc-skip-on-image-btn d-md-none"
              aria-label={t('quiz.skipQuestionBtn', 'Skip Question')}
              title={t('quiz.skipQuestionBtn', 'Skip Question')}
            >
              <i className="bi bi-skip-forward" aria-hidden />
            </Button>
            {imgSrc ? (
              <>
                {!witcImageRevealReady && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ zIndex: 2, backgroundColor: 'rgba(255, 255, 255, 0.4)' }}
                  >
                    <Spinner animation="border" role="status" variant="primary" aria-label={t('witc.loadingImage', 'Loading image')} />
                  </div>
                )}
                <div className="witc-silhouette-frame w-100 h-100 d-flex align-items-center justify-content-center">
                  <img
                    key={`${currentQuestionIndex}-${witcImageExt}-${imgSrc}`}
                    src={imgSrc}
                    alt=""
                    className={`witc-image-silhouette d-block ${
                      witcImageRevealReady
                        ? 'witc-image-silhouette--masked-visible'
                        : 'witc-image-silhouette--masked-pending'
                    }`}
                    decoding="async"
                    onLoad={handleWitcImageLoaded}
                    onError={() => {
                      if (witcImageExt === 'webp' && witcImageUrls) {
                        setWitcImageExt('png');
                      } else {
                        setWitcImageRevealReady(true);
                      }
                    }}
                    style={{
                      filter: revealFullImage ? 'none' : WITC_SILHOUETTE_FILTER_CSS,
                      transition: revealFullImage ? 'filter 0.25s ease' : undefined,
                    }}
                  />
                  {exhaustRevealAnswer !== null && (
                    <div className="witc-exhaust-answer-ribbon" aria-live="polite">
                      {exhaustRevealAnswer}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-muted p-4 text-center small">{t('witc.missingImage', 'Image not configured')}</div>
            )}
          </div>
          <p
            className={`small mb-2 fw-medium witc-points-left ${
              errorCount === 0 ? 'witc-points-left--full' : 'witc-points-left--reduced'
            }`}
          >
            {i18n.t('witc.pointsForQuestion', { count: pointsLeft })}
          </p>
          {(playBandLabel === 'Medium' || playBandLabel === 'Easy') &&
            witcMediumSkeleton &&
            errorCount >= 1 &&
            exhaustRevealAnswer === null && (
              <WitcMediumAnswerHintStrip
                skeleton={witcMediumSkeleton}
                revealedByIndex={mediumHintByIndex}
              />
            )}
          <Form
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmitAnswer();
            }}
          >
            <div className="d-flex flex-row flex-nowrap gap-2 align-items-end w-100">
              <Form.Control
                ref={answerInputRef}
                type="text"
                name={`witc-solo-answer-${quizId}-${currentQuestionIndex}`}
                value={inputValue}
                onChange={handleWitcAnswerChange}
                onPaste={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
                placeholder={t('witc.typeAnswerPlaceholder', 'Type the answer')}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                disabled={submitting || savingQuizAttempt || exhaustRevealAnswer !== null}
                aria-label={t('witc.typeAnswerPlaceholder', 'Type the answer')}
                className={`witc-answer-input flex-grow-1 min-w-0 ${wrongFlash ? 'witc-answer-input--flash' : ''} ${correctFlash ? 'witc-answer-input--flash-success' : ''}`}
              />
              <Button
                type="submit"
                variant="primary"
                className="flex-shrink-0"
                disabled={submitting || savingQuizAttempt || exhaustRevealAnswer !== null || !inputValue.trim()}
              >
                {submitting ? <Spinner size="sm" animation="border" /> : t('witc.send', 'Send')}
              </Button>
            </div>
          </Form>

          <div className="quiz-stats-actions-container">
            <div className="quiz-stats-inline">
              <div className="stat-line">
                {t('quiz.score', 'Score')}: {headerScore}
              </div>
              <div className="stat-line">
                {t('quiz.correctAnswers', 'Correct')}: {headerCorrect}/{headerQuestionTotal}
              </div>
            </div>
            <div className="quiz-actions quiz-footer-actions">
              <Button
                variant="outline-secondary"
                onClick={onQuizSkipClick(handleSkipQuestion)}
                className="quiz-action-btn d-none d-md-inline-flex align-items-center"
                disabled={savingQuizAttempt || exhaustRevealAnswer !== null}
              >
                <i className="bi bi-skip-forward me-1 me-sm-2" />
                {t('quiz.skipQuestionBtn', 'Skip Question')}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  if (embeddedInPvp) {
    return witcPlayBody;
  }

  return (
    <div className="quiz-page">
      {witcPlayBody}

      {savingQuizAttempt && (
        <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="quiz-finish-saving-overlay__inner">
            <Spinner animation="border" role="presentation" />
            <span className="text-white mt-2 small">
              {t('quiz.savingResults', 'Saving results...')}
            </span>
          </div>
        </div>
      )}

      <Modal show={showQuitModal} onHide={() => setShowQuitModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('quiz.quitQuizModal', 'Quit Quiz?')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('quiz.quitQuizBody', 'Your progress will be lost.')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuitModal(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="danger" onClick={confirmQuit}>
            {t('quiz.quitConfirm', 'Quit')}
          </Button>
        </Modal.Footer>
      </Modal>

      <SoloQuizResultsModal
        show={showResultsModal && !suppressResultsModal}
        hasTooManySkips={hasTooManySkips}
        isQuizCompleted={isQuizCompleted}
        quizTitleDisplay={getQuizFullDisplayCaption(quiz, i18n.language)}
        correctAnswers={correctAnswers}
        totalQuestions={questions.length}
        score={score}
        earnedXP={earnedXP}
        quizResponse={quizResponse}
        resultDurationSeconds={resultDurationSeconds}
        onPlayAgain={onPlayAgain}
        onBackToHome={() => void handleBackToHome()}
        playAgainLoading={playAgainLoading}
        arenaPublishOffer={arenaPublishOffer}
        arenaPublishChecked={arenaPublishChecked}
        onArenaPublishCheckedChange={setArenaPublishChecked}
        arenaPublishAvailabilityLoading={arenaHostedSummaryLoading}
        arenaPublishBlocked={arenaPublishBlocked}
        arenaPublishBlockedReason={arenaPublishBlockedReason}
        arenaPublishMaxOpenRooms={hostedOpenSummaryForArena?.maxOpenRooms}
      />

      <QuizFinishCelebrationModal
        show={showCelebration}
        onFinished={handleCelebrationFinished}
        achievements={celebrationAchievements}
        characters={celebrationCharacters}
        loginStreak={celebrationLoginStreak}
        playerNickname={celebrationPlayerNick}
      />

      <LevelUpCelebrationModal
        show={levelUpCelebration.showLevelUp}
        oldLevel={levelUpCelebration.levelUpOld}
        newLevel={levelUpCelebration.levelUpNew}
        onClose={handleLevelUpFinished}
      />
    </div>
  );
};

export default WitcQuizPlayer;
